import type { AddressVerdict, DecisionBy } from '@bartizo/shared';
import { formatRelative } from './time.js';

/**
 * What an address has been doing, told in a few sentences from the facts the
 * API keeps about it: when it was first and last seen, its events by category,
 * its request count, where it is banned and by whom, and what people asked for.
 *
 * Never a claim about one request. Raw log lines stay on the servers (rule 19),
 * so there is no "it requested /x with header y" to tell, and the story says so
 * rather than leave a gap the reader fills in.
 */

const BY: Record<DecisionBy, string> = {
  crowdsec: 'CrowdSec',
  bartizo: 'Bartizo',
  manual: 'someone on the server',
  other: 'another source',
};

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

const list = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`;

export function addressStory(verdict: AddressVerdict): string[] {
  const story: string[] = [];

  if (verdict.firstSeenAt === null && verdict.events === 0 && verdict.requests === 0) {
    story.push('Bartizo has no record of this address on any server.');
  } else if (verdict.firstSeenAt !== null && verdict.lastSeenAt !== null) {
    story.push(
      `First seen ${formatRelative(verdict.firstSeenAt)}, last seen ${formatRelative(verdict.lastSeenAt)}.`,
    );
  }

  if (verdict.events > 0) {
    const categories = Object.entries(verdict.categories)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([category, n]) => `${category} (${n.toLocaleString('en-US')})`);
    const seenOn = verdict.servers.filter((server) => server.events > 0).length;
    story.push(
      `In the last week it raised ${plural(verdict.events, 'event')} on ${plural(seenOn, 'server')}${
        categories.length > 0 ? `, most of them ${list(categories)}` : ''
      }.`,
    );
  }

  if (verdict.requests > 0) {
    const sites = verdict.servers.filter((server) => server.requests > 0).length;
    story.push(
      `In the last day it made ${plural(verdict.requests, 'request')} to websites on ${plural(sites, 'server')}.`,
    );
  }

  const bans = verdict.servers.flatMap((server) =>
    server.bans.map((ban) => ({ server: server.serverName, by: ban.by })),
  );
  if (bans.length > 0) {
    const by = [...new Set(bans.map((ban) => BY[ban.by]))];
    const servers = [...new Set(bans.map((ban) => ban.server))];
    story.push(
      `It is banned on ${plural(servers.length, 'server')}, by ${list(by)}${
        verdict.status === 'partly' ? '; the rest still let it in' : ''
      }.`,
    );
  } else if (verdict.status === 'open' && (verdict.events > 0 || verdict.requests > 0)) {
    story.push('Nothing stops it anywhere yet.');
  }

  const asked = verdict.actions.length;
  if (asked > 0) {
    story.push(`People asked for ${plural(asked, 'ban or unban', 'bans and unbans')} of it.`);
  }

  return story;
}

/** What this page cannot say, and why, so a missing detail is not read as nothing. */
export const NOT_KEPT =
  'Individual requests are not kept: the agents send counts per minute, never log lines, so there are no headers, parameters, response times or user agents per request to show.';
